import { useI18n as useNativeI18n } from 'vue-i18n'
import type { Composer, DefaultLocaleMessageSchema, NamedValue, TranslateOptions, UseI18nOptions } from 'vue-i18n'

type DeclaredProperties<Value> = {
  [Key in keyof Value as string extends Key ? never : number extends Key ? never : Key]: Value[Key]
}

/** Only declared string leaves are messages; an absent schema has no valid keys. */
export type MessageKeys<Schema> = Schema extends readonly unknown[] ? never : {
  [Key in keyof DeclaredProperties<Schema> & string]: Schema[Key] extends string
    ? Key
    : Schema[Key] extends object ? `${Key}.${MessageKeys<Schema[Key]>}` : never
}[keyof DeclaredProperties<Schema> & string]

export type MessageKey = MessageKeys<DefaultLocaleMessageSchema>

/** Preserve native translation arguments without its arbitrary-string key overload. */
interface StrictTranslate<Key extends string> {
  (key: Key): string
  (key: Key, plural: number): string
  (key: Key, plural: number, options: TranslateOptions): string
  (key: Key, defaultMessage: string): string
  (key: Key, defaultMessage: string, options: TranslateOptions): string
  (key: Key, list: unknown[]): string
  (key: Key, list: unknown[], plural: number): string
  (key: Key, list: unknown[], defaultMessage: string): string
  (key: Key, list: unknown[], options: TranslateOptions): string
  (key: Key, named?: NamedValue): string
  (key: Key, named: NamedValue, plural: number): string
  (key: Key, named: NamedValue, defaultMessage: string): string
  (key: Key, named: NamedValue, options: TranslateOptions): string
}

type OptionMessages<Options extends UseI18nOptions> = NonNullable<Options['messages']>
type StrictComposer<Options extends UseI18nOptions> = Omit<Composer<
  OptionMessages<Options>,
  NonNullable<Options['datetimeFormats']>,
  NonNullable<Options['numberFormats']>,
  string
>, 't'> & {
  t: StrictTranslate<MessageKey | MessageKeys<OptionMessages<Options>[keyof OptionMessages<Options>]>>
}

// This checked assignment keeps the native function, composer and reactive members
// unchanged. Intersecting native `t` with StrictTranslate would retain its escape.
export const useI18n: <Options extends UseI18nOptions = UseI18nOptions>(options?: Options) => StrictComposer<Options> = useNativeI18n
